import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ListingHeader } from "../../../components/listing-header";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import axiosService from "../../../core/services/axios-service";
import { getInitials, isRescueRnAdmin } from "../../../core/services/global-functions";
import { applicationRole } from "../../../core/dictionary/global-enums";
import ToastService from "../../../core/services/toaster-service";
import { RescueRnTable } from "../../../components/designs/rescueRn-table";
import Loader from "../../../components/designs/loader";
import { Users, CheckCircle2, Clock, UserPlus } from "lucide-react";

// "day1" → "Day 1", "afternoon" → "Afternoon"
function formatLabel(val) {
  return val
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ManageAttendancePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [organizationName, setOrganizationName] = useState("");
  const [day, setDay] = useState("");
  const [session, setSession] = useState("");

  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [trainingId, setTrainingId] = useState(null);

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");

  // attendance map: { [memberId]: "present" | "absent" }
  const [attendance, setAttendance] = useState({});
  const [markingId, setMarkingId] = useState(null);

  // Redirect non-admin users away
  useEffect(() => {
    if (!isRescueRnAdmin()) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  // Fetch offline slots from schedule API on page load
  useEffect(() => {
    if (!id) return;
    setScheduleLoading(true);
    axiosService
      .get(`${ApiRoutes.TRAININGS_BY_ORGANIZATION}/${id}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const offlineRecord = res.data.data.find((r) => r.type === "offline");
          setScheduleSlots(offlineRecord?.offlineSlots ?? []);
          setTrainingId(offlineRecord?._id ?? null);
        }
      })
      .catch(() => ToastService.error("Failed to load schedule."))
      .finally(() => setScheduleLoading(false));
  }, [id]);

  // Re-fetch when day+session or pagination/sort changes
  useEffect(() => {
    if (day && session) {
      fetchMembers(currentPage, perPage, sortBy, sortOrder);
    } else {
      setData([]);
      setTotalRows(0);
      setAttendance({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, session, currentPage, perPage, sortBy, sortOrder]);

  const fetchMembers = async (page, size, sort, order) => {
    setLoading(true);
    try {
      // 1. Fetch member list
      const res = await axiosService.get(
        `${ApiRoutes.COACH_TEAM_MEMBER_LISTING}/${id}`,
        {
          params: {
            perPage: size,
            currentPage: page,
            sortBy: sort,
            sortOrder: order,
          },
        }
      );
      const list = res.data.data || [];
      setOrganizationName(
        res.data.organization_name || list[0]?.organization_name || ""
      );
      setData(list);
      setTotalRows(res.data.total || 0);

      // 2. Fetch attendance records for this training
      const attMap = {};
      if (trainingId) {
        const attRes = await axiosService.get(
          `${ApiRoutes.GET_ATTENDANCE}/${trainingId}`
        );
        if (attRes.data?.success && Array.isArray(attRes.data.data)) {
          attRes.data.data
            .filter((r) => r.day === day && r.session === session)
            .forEach((r) => {
              if (r.userId?._id) attMap[r.userId._id] = r.status;
            });
        }
      }

      // 3. Merge: members without a record default to "absent"
      setAttendance(() => {
        const next = { ...attMap };
        list.forEach((m) => {
          if (!(m._id in next)) next[m._id] = "absent";
        });
        return next;
      });
    } catch {
      ToastService.error("Failed to load members.");
    } finally {
      setLoading(false);
    }
  };

  const markAttendance = async (memberId, status) => {
    setMarkingId(memberId);
    try {
      const slot = scheduleSlots.find((s) => s.day === day && s.session === session);
      await axiosService.post(ApiRoutes.MARK_ATTENDANCE, {
        trainingId,
        userId: memberId,
        slotId: slot?._id,
        status,
      });
      setAttendance((prev) => ({ ...prev, [memberId]: status }));
      ToastService.success(status === "present" ? "Marked as Present" : "Marked as Absent");
    } catch {
      ToastService.error("Failed to mark attendance.");
    } finally {
      setMarkingId(null);
    }
  };

  const markPresent = (memberId) => markAttendance(memberId, "present");
  const markAbsent = (memberId) => markAttendance(memberId, "absent");

  const getRoleLabel = (role) =>
    role === applicationRole.coach
      ? "Coach"
      : role === applicationRole.teamMember
      ? "Team Member"
      : role;

  const presentCount = Object.values(attendance).filter((s) => s === "present").length;
  const absentCount = Object.values(attendance).filter((s) => s === "absent").length;

  const columns = [
    {
      name: "Name",
      selector: (row) => row.fullName,
      sortable: true,
      sortField: "fullName",
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="organization-avatar me-3">
            {getInitials(row.fullName)}
          </div>
          {row.fullName}
        </div>
      ),
      width: "220px",
    },
    {
      name: "Role",
      selector: (row) => row.role,
      sortable: true,
      sortField: "role",
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {getRoleLabel(row.role === "team_member" ? "Team Member" : row.role === "coach" ? "Coach" : row.role) || "—"}
        </span>
      ),
    },
    {
      name: "Department",
      selector: (row) => row.department,
      sortable: true,
      sortField: "department",
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.department || "—"}
        </span>
      ),
    },
    {
      name: "Status",
      cell: (row) => {
        const isPresent = attendance[row._id] === "present";
        return isPresent ? (
          <span className="attendance-badge attendance-badge--present">
            <CheckCircle2 size={13} />
            Present
          </span>
        ) : (
          <span className="attendance-badge attendance-badge--absent">
            <Clock size={13} />
            Absent
          </span>
        );
      },
    },
    {
      name: "Action",
      cell: (row) => {
        const isPresent = attendance[row._id] === "present";
        const isMarking = markingId === row._id;
        return isPresent ? (
          <button
            className="btn attendance-btn-absent"
            disabled={isMarking}
            onClick={() => markAbsent(row._id)}
          >
            {isMarking ? "..." : "Mark Absent"}
          </button>
        ) : (
          <button
            className="btn attendance-btn-present"
            disabled={isMarking}
            onClick={() => markPresent(row._id)}
          >
            {isMarking ? "..." : "Mark Present"}
          </button>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  return (
    <>
      <ListingHeader
        title="Manage Attendance"
        breadCrumb={[
          { label: "Dashboard", href: "/admin/dashboard" },
          { label: "Organizations", href: "/admin/organizations" },
          {
            label: organizationName || "Organization",
            href: `/admin/organizations/${id}`,
          },
          { label: "Manage Attendance", href: "#" },
        ]}
        actions={[
          {
            label: "Add Coach",
            className: "btn btn-rescuern-primary",
            icon: UserPlus,
            onClick: () => window.open(`/signup/coach/${id}`, "_blank"),
          },
          {
            label: "Add Team Member",
            className: "btn btn-rescuern-primary",
            icon: UserPlus,
            onClick: () => window.open(`/signup/team-member/${id}`, "_blank"),
          },
        ]}
        dropdown={null}
      />

      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">

          {/* ── Day + Session Selectors ── */}
          <div className="card attendance-selector-card mb-4">
            <div className="card-body">
              {scheduleLoading ? (
                <Loader text="Loading schedule..." />
              ) : (
                <div className="manage-schedule-form">
                  <div className="row g-4">
                    {/* Day — derived from offlineSlots */}
                    <div className="col-md-6">
                      <p className="attendance-selector-label">Select Day</p>
                      <div className="d-flex gap-3 flex-wrap">
                        {[...new Set(scheduleSlots.map((s) => s.day))].map((dayVal) => (
                          <div
                            key={dayVal}
                            className={`form-check${day === dayVal ? " form-check--selected" : ""}`}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              name="attendance-day"
                              id={`att-day-${dayVal}`}
                              value={dayVal}
                              checked={day === dayVal}
                              onChange={() => {
                                setDay(dayVal);
                                setSession("");
                                setCurrentPage(1);
                              }}
                            />
                            <label className="form-check-label" htmlFor={`att-day-${dayVal}`}>
                              {formatLabel(dayVal)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Session — filtered by selected day */}
                    {day && (
                      <div className="col-md-6">
                        <p className="attendance-selector-label">Select Session</p>
                        <div className="d-flex gap-3 flex-wrap">
                          {[...new Set(
                            scheduleSlots
                              .filter((s) => s.day === day)
                              .map((s) => s.session)
                          )].map((sessionVal) => (
                            <div
                              key={sessionVal}
                              className={`form-check${session === sessionVal ? " form-check--selected" : ""}`}
                            >
                              <input
                                className="form-check-input"
                                type="radio"
                                name="attendance-session"
                                id={`att-session-${sessionVal}`}
                                value={sessionVal}
                                checked={session === sessionVal}
                                onChange={() => {
                                  setSession(sessionVal);
                                  setCurrentPage(1);
                                }}
                              />
                              <label className="form-check-label" htmlFor={`att-session-${sessionVal}`}>
                                {formatLabel(sessionVal)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Attendance List ── */}
          {day && session ? (
            <>
              {/* Summary chips */}
              {!loading && data.length > 0 && (
                <div className="d-flex gap-3 mb-3 flex-wrap">
                  <div className="attendance-summary-chip attendance-summary-chip--total">
                    <Users size={15} />
                    <span>Total: {totalRows}</span>
                  </div>
                  <div className="attendance-summary-chip attendance-summary-chip--present">
                    <CheckCircle2 size={15} />
                    <span>Present: {presentCount}</span>
                  </div>
                  <div className="attendance-summary-chip attendance-summary-chip--absent">
                    <Clock size={15} />
                    <span>Absent: {absentCount}</span>
                  </div>
                </div>
              )}

              <RescueRnTable
                data={data}
                loading={loading}
                columns={columns}
                totalRows={totalRows}
                loadingTableText="Loading attendance..."
                handleSort={(column, direction) => {
                  setSortBy(column.sortField || "createdAt");
                  setSortOrder(direction || "asc");
                }}
                onChangePage={(page) => setCurrentPage(page)}
                handlePerRowsChange={(newPerPage) => {
                  setPerPage(newPerPage);
                  setCurrentPage(1);
                }}
                perPage={perPage}
              />
            </>
          ) : (
            /* Prompt when no selection yet */
            <div className="attendance-prompt">
              <Users size={44} />
              <p>
                {!day
                  ? "Select a Day to get started."
                  : "Now select a Session to load the attendance list."}
              </p>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
